import { useCallback, useState } from 'react';
import { Upload, FileText, Image, File, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UploadZone({ onFileUpload, isProcessing }) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFileToServer = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(xhr.statusText || 'Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });
      });

      xhr.open('POST', `${BACKEND_URL}/api/upload`);
      xhr.send(formData);

      const response = await uploadPromise;
      
      setIsUploading(false);
      setUploadProgress(100);
      
      // Pass the file data to parent
      onFileUpload(response);
      
      return response;
    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file');
      throw error;
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    if (isProcessing || isUploading) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFileToServer(files[0]);
    }
  }, [isProcessing, isUploading]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = async (e) => {
    if (isProcessing || isUploading) return;
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await uploadFileToServer(files[0]);
    }
  };

  const disabled = isProcessing || isUploading;

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className="text-center"
    >
      <div className="flex justify-center mb-6">
        <div className={`w-20 h-20 rounded-2xl bg-gradient-primary flex items-center justify-center ${!disabled ? 'animate-float' : ''}`}>
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-primary-foreground animate-spin" />
          ) : (
            <Upload className="w-10 h-10 text-primary-foreground" />
          )}
        </div>
      </div>
      
      <h3 className="text-xl font-semibold text-foreground mb-2">
        {isUploading ? 'Uploading...' : 'Upload anything'}
      </h3>
      <p className="text-muted-foreground mb-6">
        {isUploading 
          ? `${uploadProgress}% complete` 
          : 'Drag and drop or click to browse'}
      </p>

      {isUploading && (
        <div className="mb-6">
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}

      <input
        type="file"
        id="file-upload"
        className="hidden"
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.txt"
        disabled={disabled}
      />
      
      <label htmlFor="file-upload">
        <Button
          as="span"
          size="lg"
          disabled={disabled}
          className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary cursor-pointer"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Uploading...
            </>
          ) : isProcessing ? (
            'Processing...'
          ) : (
            'Choose File'
          )}
        </Button>
      </label>

      <div className="mt-8 flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <FileText className="w-4 h-4" />
          <span>Documents</span>
        </div>
        <div className="flex items-center gap-1">
          <Image className="w-4 h-4" />
          <span>Screenshots</span>
        </div>
        <div className="flex items-center gap-1">
          <File className="w-4 h-4" />
          <span>PDFs</span>
        </div>
      </div>
    </div>
  );
}
